class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this._lastRenderTime = 0;
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this._initSwipeToDismiss();
    this._initProgressCollapse();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    const panel = this.querySelector('.drawer__inner');
    if (panel) panel.classList.remove('drawer--expanded');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // Fix 4: temporal guard — skip if rendered less than 500ms ago
    var now = Date.now();
    if (now - this._lastRenderTime < 500) return;
    this._lastRenderTime = now;

    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  _initProgressCollapse() {
    const self = this;
    this.addEventListener('scroll', (e) => {
      const scrollable = e.target.closest('cart-drawer-items');
      if (!scrollable) return;
      const progress = self.querySelector('.cart-drawer-progress');
      if (!progress) return;
      const scrolle = scrollable.scrollTop > 5;
      // Accordéon upsell (toutes tailles) : replié au scroll, ré-ouvert en haut.
      progress.classList.toggle('cart-drawer-progress--replie', scrolle);
      if (!scrolle) {
        const upsell = progress.querySelector('cart-drawer-upsell');
        if (upsell) upsell.classList.remove('cart-drawer-upsell--ouvert');
      }
      // Message compact : mobile uniquement (bottom sheet).
      if (window.innerWidth <= 749) {
        progress.classList.toggle('cart-drawer-progress--compact', scrolle);
      }
    }, true);
  }

  _initSwipeToDismiss() {
    let startY = 0;
    let isDragging = false;
    let panel = null;
    const self = this;

    this.addEventListener('touchstart', (e) => {
      if (window.innerWidth > 749) return;
      panel = this.querySelector('.drawer__inner');
      if (!panel) return;
      const scrollable = panel.querySelector('cart-drawer-items');
      if (scrollable && scrollable.scrollTop > 0) return;
      startY = e.touches[0].clientY;
      isDragging = true;
    }, { passive: true });

    this.addEventListener('touchmove', (e) => {
      if (!isDragging || !panel) return;
      const deltaY = e.touches[0].clientY - startY;
      const scrollable = panel.querySelector('cart-drawer-items');
      if (scrollable && scrollable.scrollTop > 0) {
        isDragging = false;
        panel.style.transition = 'none';
        panel.style.transform = '';
        return;
      }
      if (deltaY > 0) {
        panel.style.transition = 'none';
        panel.style.transform = `translateY(${deltaY}px)`;
      }
    }, { passive: true });

    this.addEventListener('touchend', (e) => {
      if (!isDragging || !panel) { isDragging = false; return; }
      isDragging = false;
      const deltaY = e.changedTouches[0].clientY - startY;
      const isExpanded = panel.classList.contains('drawer--expanded');
      panel.style.transition = '';

      if (deltaY > 80) {
        if (isExpanded) {
          panel.style.transform = '';
          panel.classList.remove('drawer--expanded');
        } else {
          panel.style.transform = 'translateY(100%)';
          panel.addEventListener('transitionend', () => {
            self.close();
            panel.style.transform = '';
            panel.classList.remove('drawer--expanded');
          }, { once: true });
        }
      } else if (deltaY < -60 && !isExpanded) {
        panel.classList.add('drawer--expanded');
      } else {
        panel.style.transform = '';
      }
    });
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
